import { useCallback, useEffect, useState } from "react";
import { api, AUDIT_ACAO, AUDIT_ENTITY, fmtDate } from "@/lib/api";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollText, Search } from "lucide-react";

const EMPTY = { entity: "todas", q: "", date_from: "", date_to: "" };

const ACAO_STYLE = {
  criado: "text-emerald-400 bg-emerald-400/10",
  alterado: "text-sky-400 bg-sky-400/10",
  excluido: "text-rose-400 bg-rose-400/10",
  status_alterado: "text-amber-400 bg-amber-400/10",
  parecer: "text-indigo-400 bg-indigo-400/10",
};

export default function AuditoriaPage() {
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState(EMPTY);

  const load = useCallback(() => {
    const params = {};
    if (filters.entity !== "todas") params.entity = filters.entity;
    if (filters.q.trim()) params.q = filters.q.trim();
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    api.get("/tenant/reports/audit", { params }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  if (!items) return <Spinner />;

  return (
    <div data-testid="auditoria-page">
      <PageHeader title="Auditoria" subtitle="Log de inclusões, alterações e exclusões — Pedidos/Ocorrências, Avisos, Chamados, Horários e Reservas" />

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-3 mb-4 space-y-3" data-testid="audit-filters">
        <div className="relative">
          <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input data-testid="audit-filter-q" value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Buscar por item, descrição ou usuário…"
            className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-zinc-400">Módulo</Label>
            <Select value={filters.entity} onValueChange={(v) => setFilters({ ...filters, entity: v })}>
              <SelectTrigger data-testid="audit-filter-entity" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem data-testid="audit-filter-entity-todas" value="todas">Todos</SelectItem>
                {Object.entries(AUDIT_ENTITY).map(([k, label]) => (
                  <SelectItem key={k} data-testid={`audit-filter-entity-${k}`} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">De</Label>
            <Input type="date" data-testid="audit-filter-from" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-40 [color-scheme:dark]" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Até</Label>
            <Input type="date" data-testid="audit-filter-to" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-40 [color-scheme:dark]" />
          </div>
          {(filters.entity !== "todas" || filters.q || filters.date_from || filters.date_to) && (
            <Button variant="ghost" data-testid="audit-filter-clear" onClick={() => setFilters(EMPTY)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              Limpar
            </Button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={ScrollText} title="Nenhum registro de auditoria"
          subtitle="As inclusões, alterações e exclusões passam a ser registradas automaticamente." />
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Data/Hora</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Usuário</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Módulo</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Item</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Ação</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Descrição</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`audit-row-${a.id}`}
                  className="border-b border-zinc-800/60 hover:bg-zinc-800/50 transition-colors duration-200">
                  <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{fmtDate(a.created_at)}</td>
                  <td className="px-4 py-3 text-zinc-300 text-xs">{a.usuario_nome}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{AUDIT_ENTITY[a.entity] || a.entity}</td>
                  <td className="px-4 py-3 text-zinc-200 text-xs max-w-52 truncate" title={a.entity_label}>{a.entity_label}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 ${ACAO_STYLE[a.acao] || "text-zinc-400 bg-zinc-400/10"}`}
                      data-testid={`audit-acao-${a.id}`}>
                      {AUDIT_ACAO[a.acao] || a.acao}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs max-w-64 truncate" title={a.descricao}>{a.descricao}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
